/**
 * Authentication API Module
 * Handles all auth-related API calls
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Storage key for auth token
const TOKEN_KEY = 'deal_intel_token';
const USER_KEY = 'deal_intel_user';

/**
 * Get stored auth token
 */
export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored user
 */
export const getStoredUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Store auth data
 */
export const storeAuthData = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Clear auth data
 */
export const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Create authenticated axios instance
 */
const createAuthApi = () => {
  const instance = axios.create({
    baseURL: API_BASE,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth token to requests
  instance.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 responses
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        clearAuthData();
        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const authApi = createAuthApi();

// ============== AUTH ENDPOINTS ==============

export const authService = {
  /**
   * Login with email and password
   */
  login: async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, {
      email,
      password,
    });
    const { access_token, user } = response.data;
    storeAuthData(access_token, user);
    return response.data;
  },

  /**
   * Logout current user
   */
  logout: async () => {
    try {
      await authApi.post('/auth/logout');
    } catch (e) {
      // Ignore logout errors
    }
    clearAuthData();
  },

  /**
   * Check authentication status
   */
  checkStatus: async () => {
    const token = getStoredToken();
    if (!token) {
      return { authenticated: false, user: null };
    }
    
    try {
      const response = await authApi.get('/auth/status');
      return response.data;
    } catch {
      clearAuthData();
      return { authenticated: false, user: null };
    }
  },

  /**
   * Get current user info
   */
  getCurrentUser: async () => {
    const response = await authApi.get('/auth/me');
    return response.data;
  },
};

// ============== USER MANAGEMENT (ADMIN) ==============

export const usersApi = {
  /**
   * List all users (admin only)
   */
  list: async (includeInactive = true) => {
    const response = await authApi.get('/users', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  /**
   * Get single user (admin only)
   */
  getById: async (userId) => {
    const response = await authApi.get(`/users/${userId}`);
    return response.data;
  },

  /**
   * Create new user (admin only)
   */
  create: async (userData) => {
    const response = await authApi.post('/users', userData);
    return response.data;
  },

  /**
   * Update user (admin only)
   */
  update: async (userId, userData) => {
    const response = await authApi.put(`/users/${userId}`, userData);
    return response.data;
  },

  /**
   * Activate user (admin only)
   */
  activate: async (userId) => {
    const response = await authApi.post(`/users/${userId}/activate`);
    return response.data;
  },

  /**
   * Deactivate user (admin only)
   */
  deactivate: async (userId) => {
    const response = await authApi.post(`/users/${userId}/deactivate`);
    return response.data;
  },

  /**
   * Reset user password (admin only)
   */
  resetPassword: async (userId, newPassword, newPasswordConfirm) => {
    const response = await authApi.post(`/users/${userId}/reset-password`, {
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    });
    return response.data;
  },
};

// ============== PROFILE (SELF-SERVICE) ==============

export const profileApi = {
  /**
   * Get current user profile
   */
  get: async () => {
    const response = await authApi.get('/profile');
    return response.data;
  },

  /**
   * Update profile
   */
  update: async (profileData) => {
    const response = await authApi.put('/profile', profileData);
    return response.data;
  },

  /**
   * Change password
   */
  changePassword: async (currentPassword, newPassword, newPasswordConfirm) => {
    const response = await authApi.post('/profile/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    });
    return response.data;
  },
};

export default authApi;
